/**
 * AdminAsoUploadTab — full-screen ASO management view.
 *
 * Layout:
 *   • Header with 4 status counters (Vencidos / ≤30d / OK / Sem cadastro)
 *   • Search box + status filter chips
 *   • Table: MATRÍCULA · NOME · POSTO · VENCIMENTO · DIAS PARA VENCER
 *   • Discrete "Importar planilha" button top-right that opens a modal
 *
 * The upload modal is the previous full-page upload experience, now
 * compacted into a Dialog.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Upload, Loader2, Stethoscope, FileSpreadsheet, AlertTriangle,
  CheckCircle2, X, Search, RefreshCw, Mail, Send, Trash2, Save,
  Paperclip, FileText, Image as ImageIcon,
} from "lucide-react";
import { API, useAuth } from "../App";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";

const FILTERS = [
  { id: "all", label: "Todos", color: "slate" },
  { id: "expired", label: "Vencidos", color: "red" },
  { id: "soon", label: "≤ 30 dias", color: "amber" },
  { id: "ok", label: "Em dia", color: "emerald" },
  { id: "missing", label: "Sem cadastro", color: "gray" },
];

const isoToBr = (iso) => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const rowStyle = (du) => {
  if (du === null || du === undefined) return "";
  if (du < 0) return "bg-red-50/50 hover:bg-red-100";
  if (du <= 30) return "bg-amber-50/50 hover:bg-amber-100";
  return "";
};

const daysBadge = (du) => {
  if (du === null || du === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
        —
      </span>
    );
  }
  if (du < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 font-semibold">
        <AlertTriangle className="w-3 h-3" /> Venc. há {-du} dia{-du !== 1 ? "s" : ""}
      </span>
    );
  }
  if (du <= 30) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 font-semibold">
        <AlertTriangle className="w-3 h-3" /> Vence em {du} dia{du !== 1 ? "s" : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
      <CheckCircle2 className="w-3 h-3" /> {du} dias
    </span>
  );
};

export default function AdminAsoUploadTab() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0, expired: 0, soon: 0, ok: 0, missing: 0,
  });
  const [today, setToday] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [notifyOpen, setNotifyOpen] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (filter !== "all") params.status = filter;
      const r = await axios.get(`${API}/admin/aso/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params, timeout: 30000,
      });
      setItems(r.data.items || []);
      setStats(r.data.stats || {});
      setToday(r.data.today || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao carregar lista");
    } finally { setLoading(false); }
  }, [token, search, filter]);

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
  }, [fetchList]);

  const selectedCount = selectedIds.size;
  const currentPageIds = items.map((it) => it.id);
  const allSelected = currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedIds.has(id));
  const someSelected = currentPageIds.some((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) currentPageIds.forEach((id) => next.delete(id));
      else currentPageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());
  const selectAllExpired = () => {
    setSelectedIds(new Set(items
      .filter((it) => it.days_until_expiry !== null && it.days_until_expiry <= 30)
      .map((it) => it.id)));
  };

  return (
    <div className="space-y-4" data-testid="admin-aso-tab">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-emerald-700" />
          <h2 className="text-base font-heading font-semibold text-slate-900">
            Consulta de ASOs
          </h2>
          <span className="text-xs text-slate-500">
            Ref.: hoje ({isoToBr(today)})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost" size="sm" onClick={fetchList}
            className="h-8 text-xs text-slate-600"
            data-testid="aso-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setUploadOpen(true)}
            className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="aso-open-upload-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            Importar planilha
          </Button>
        </div>
      </div>

      {/* Selection action bar */}
      {selectedCount > 0 && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 p-2.5 flex items-center justify-between flex-wrap gap-2"
          data-testid="aso-selection-bar"
        >
          <div className="text-xs sm:text-sm text-blue-900 font-semibold">
            {selectedCount} funcionário(s) selecionado(s)
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm" variant="ghost"
              onClick={clearSelection}
              className="h-7 text-xs text-slate-600"
              data-testid="aso-clear-selection"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Limpar
            </Button>
            <Button
              size="sm"
              onClick={() => setNotifyOpen(true)}
              className="h-8 text-xs bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="aso-notify-btn"
            >
              <Mail className="w-3.5 h-3.5 mr-1" />
              Comunicar à Clínica
            </Button>
          </div>
        </div>
      )}

      {/* Stat pills */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <StatPill label="Total" value={stats.total} color="slate" />
        <StatPill label="Vencidos" value={stats.expired} color="red" testId="aso-stat-expired" />
        <StatPill label="≤ 30 dias" value={stats.soon} color="amber" testId="aso-stat-soon" />
        <StatPill label="Em dia" value={stats.ok} color="emerald" />
        <StatPill label="Sem cadastro" value={stats.missing} color="gray" />
      </div>

      {/* Search + filter chips */}
      <Card className="border-slate-200">
        <CardContent className="p-3 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, matrícula ou posto…"
              className="pl-8 h-9 text-sm"
              data-testid="aso-search"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTERS.map((f) => {
              const isActive = filter === f.id;
              const activeMap = {
                slate: "bg-slate-900 text-white border-slate-900",
                red: "bg-red-600 text-white border-red-600",
                amber: "bg-amber-500 text-white border-amber-500",
                emerald: "bg-emerald-600 text-white border-emerald-600",
                gray: "bg-slate-500 text-white border-slate-500",
              };
              return (
                <button key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    isActive
                      ? activeMap[f.color]
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`aso-filter-${f.id}`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm">
              Nenhum funcionário encontrado com esse filtro.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10 py-1.5">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Selecionar todos"
                        data-testid="aso-check-all"
                        className={someSelected && !allSelected ? "data-[state=unchecked]:bg-blue-200" : ""}
                      />
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide">
                      Matrícula
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide">
                      Nome
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide">
                      Posto
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide">
                      Vencimento
                    </TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wide">
                      Dias
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow
                      key={it.id}
                      className={rowStyle(it.days_until_expiry)}
                      data-testid={`aso-row-${it.matricula}`}
                    >
                      <TableCell className="py-1.5">
                        <Checkbox
                          checked={selectedIds.has(it.id)}
                          onCheckedChange={() => toggleOne(it.id)}
                          data-testid={`aso-check-${it.matricula}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-700 py-1.5">
                        {it.matricula}
                      </TableCell>
                      <TableCell className="text-sm text-slate-900 py-1.5">
                        {it.nome}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 py-1.5">
                        {it.posto_servico || "—"}
                      </TableCell>
                      <TableCell className="text-xs text-slate-800 py-1.5 font-mono">
                        {isoToBr(it.vencimento_aso)}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {daysBadge(it.days_until_expiry)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="px-3 py-2 border-t bg-slate-50 text-[11px] text-slate-500">
              {items.length} funcionário(s) exibido(s)
            </div>
          )}
        </CardContent>
      </Card>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onImported={() => { setUploadOpen(false); fetchList(); }}
      />

      <NotifyClinicaDialog
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        selectedIds={Array.from(selectedIds)}
        selectedItems={items.filter((it) => selectedIds.has(it.id))}
        onSent={() => { setNotifyOpen(false); clearSelection(); }}
      />
    </div>
  );
}

const StatPill = ({ label, value, color, testId }) => {
  const map = {
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    gray: "bg-gray-50 border-gray-200 text-gray-700",
  };
  return (
    <div className={`border rounded-md px-3 py-1.5 ${map[color] || map.slate}`}
      data-testid={testId}>
      <div className="text-lg font-bold leading-tight">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
    </div>
  );
};

function UploadDialog({ open, onClose, onImported }) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setFile(null); setResult(null); }
  }, [open]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx?|XLSX?)$/.test(f.name)) {
      toast.error("Formato inválido. Envie XLSX ou XLS."); return;
    }
    setFile(f); setResult(null);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/employees/import-aso`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 120000,
      });
      setResult(r.data);
      toast.success(`ASOs importados: ${r.data.atualizados} funcionários atualizados`);
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao importar");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="aso-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-emerald-700" />
            Importar planilha de ASOs
          </DialogTitle>
          <DialogDescription>
            XLSX/XLS com colunas MAT, NOME, Última Consulta e Próxima
            Consulta. O sistema atualiza o vencimento do ASO batendo por
            matrícula (primeiro) e depois por nome.
          </DialogDescription>
        </DialogHeader>

        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            file
              ? "border-emerald-300 bg-emerald-50/50"
              : "border-slate-300 bg-slate-50 hover:border-emerald-300"
          }`}
        >
          {file ? (
            <div className="space-y-2">
              <FileSpreadsheet className="w-8 h-8 text-emerald-600 mx-auto" />
              <div className="font-medium text-sm text-slate-900">{file.name}</div>
              <div className="text-xs text-slate-500">
                {(file.size / 1024).toFixed(1)} KB
              </div>
              <Button variant="ghost" size="sm"
                onClick={() => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; }}
                className="text-slate-500" data-testid="aso-clear">
                <X className="w-3 h-3 mr-1" /> Trocar arquivo
              </Button>
            </div>
          ) : (
            <label className="cursor-pointer block">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <div className="text-sm text-slate-700 font-medium">
                Clique para selecionar
              </div>
              <div className="text-xs text-slate-500 mt-1">
                .xlsx ou .xls
              </div>
              <input ref={inputRef} type="file" className="hidden"
                accept=".xlsx,.xls" onChange={onPick}
                data-testid="aso-file-input" />
            </label>
          )}
        </div>

        {result && (
          <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-900">
            <div><strong>{result.atualizados}</strong> atualizados de {result.total_rows} linhas.</div>
            {result.nao_localizados_count > 0 && (
              <div className="text-red-700 mt-1">
                {result.nao_localizados_count} não localizado(s).
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={uploading}
            data-testid="aso-cancel">
            Fechar
          </Button>
          <Button
            onClick={upload}
            disabled={!file || uploading}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="aso-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const DEFAULT_SUBJECT = "AVI Segurança — Vencimento de ASO — Funcionários";
const DEFAULT_BODY =
  "Prezados,\n\n" +
  "Informamos o vencimento do ASO dos funcionários listados no documento em anexo. " +
  "Solicitamos gentilmente o agendamento dos respectivos exames médicos ocupacionais " +
  "na maior brevidade possível.\n\n" +
  "Atenciosamente,\nRH AVI Segurança";

function NotifyClinicaDialog({
  open, onClose, selectedIds, selectedItems, onSent,
}) {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [emailTo, setEmailTo] = useState("");
  const [cc, setCc] = useState("");
  const [clinicaNome, setClinicaNome] = useState("");
  const [saveContact, setSaveContact] = useState(false);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{name,size,mime,base64}]
  const fileInputRef = useRef(null);
  const MAX_TOTAL = 20 * 1024 * 1024;

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const rd = new FileReader();
      rd.onload = () => {
        const s = rd.result || "";
        const b64 = String(s).split(",", 2)[1] || "";
        resolve(b64);
      };
      rd.onerror = reject;
      rd.readAsDataURL(file);
    });

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    let current = attachments.reduce((s, a) => s + a.size, 0);
    const next = [...attachments];
    for (const f of files) {
      const ok = /^(image\/|application\/pdf$)/i.test(f.type);
      if (!ok) {
        toast.error(`Formato não suportado: ${f.name} (use PDF ou imagem).`);
        continue;
      }
      if (current + f.size > MAX_TOTAL) {
        toast.error("Anexos ultrapassaram 20MB no total.");
        break;
      }
      try {
        const base64 = await readFileAsBase64(f);
        next.push({
          name: f.name,
          size: f.size,
          mime: f.type || "application/octet-stream",
          base64,
        });
        current += f.size;
      } catch {
        toast.error(`Falha ao ler ${f.name}`);
      }
    }
    setAttachments(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const humanSize = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Reset form + load contacts when dialog opens
  useEffect(() => {
    if (!open) return;
    setEmailTo("");
    setCc("");
    setClinicaNome("");
    setSaveContact(false);
    setSubject(DEFAULT_SUBJECT);
    setBody(DEFAULT_BODY);
    setAttachments([]);
    axios
      .get(`${API}/admin/clinica-contacts`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      })
      .then((r) => setContacts(r.data?.contacts || []))
      .catch(() => setContacts([]));
  }, [open, token]);

  const pickContact = (c) => {
    setEmailTo(c.email);
    setClinicaNome(c.nome);
    setSaveContact(false);
  };

  const removeContact = async (id) => {
    if (!window.confirm("Remover este contato?")) return;
    try {
      await axios.delete(`${API}/admin/clinica-contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Contato removido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao remover");
    }
  };

  const send = async () => {
    if (!emailTo.trim()) {
      toast.error("Informe o e-mail da clínica.");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/aso/notify-clinica`,
        {
          employee_ids: selectedIds,
          email_to: emailTo.trim(),
          cc: cc.trim() || null,
          clinica_nome: clinicaNome.trim() || null,
          subject: subject.trim() || null,
          body: body.trim() || null,
          save_contact: saveContact,
          extra_attachments: attachments.map((a) => ({
            filename: a.name,
            content_base64: a.base64,
            mime_type: a.mime,
          })),
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
      );
      const ccInfo = r.data?.cc?.length
        ? ` (cc: ${r.data.cc.length})`
        : "";
      toast.success(
        `E-mail enviado para ${r.data.to}${ccInfo} com ${r.data.employees_count} funcionário(s).`,
      );
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar e-mail.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="aso-notify-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-700" />
            Comunicar à clínica
          </DialogTitle>
          <DialogDescription>
            Envie um e-mail à clínica com o PDF personalizado da AVI listando
            os {selectedIds.length} funcionário(s) selecionado(s). Os contatos
            das clínicas ficam salvos para reutilização.
          </DialogDescription>
        </DialogHeader>

        {/* Saved contacts */}
        {contacts.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
              Contatos salvos
            </div>
            <div className="flex flex-wrap gap-1.5">
              {contacts.map((c) => (
                <div key={c.id}
                  className="group inline-flex items-center gap-1 border rounded-full pl-2 pr-0.5 py-0.5 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <button
                    className="text-xs text-slate-800 pr-1"
                    onClick={() => pickContact(c)}
                    data-testid={`aso-pick-contact-${c.id}`}
                  >
                    <span className="font-medium">{c.nome}</span>
                    <span className="text-slate-500 ml-1">({c.email})</span>
                  </button>
                  <button
                    className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 p-0.5 rounded-full"
                    onClick={() => removeContact(c.id)}
                    title="Remover"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Nome da clínica</label>
              <Input
                value={clinicaNome}
                onChange={(e) => setClinicaNome(e.target.value)}
                placeholder="Ex: CLÍNICA SAÚDE OCUPACIONAL"
                data-testid="aso-notify-clinica-nome"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">E-mail *</label>
              <Input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="clinica@exemplo.com"
                data-testid="aso-notify-email"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">
              CC (com cópia){" "}
              <span className="text-slate-400 font-normal">
                — separe múltiplos e-mails com ponto e vírgula (;)
              </span>
            </label>
            <Input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="rh@empresa.com; supervisor@empresa.com"
              data-testid="aso-notify-cc"
            />
          </div>

          {clinicaNome.trim() && (
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <Checkbox
                checked={saveContact}
                onCheckedChange={(v) => setSaveContact(!!v)}
                data-testid="aso-notify-save-contact"
              />
              <Save className="w-3 h-3" />
              Salvar como contato para reutilizar depois
            </label>
          )}

          <div>
            <label className="text-xs font-medium text-slate-700">Assunto</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="aso-notify-subject"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">Corpo do e-mail</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              className="font-mono text-xs"
              data-testid="aso-notify-body"
            />
          </div>

          {/* Extra attachments (images / PDFs) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" />
                Anexos adicionais
                <span className="text-slate-400 font-normal">
                  (opcional — PDF ou imagem, até 20MB no total)
                </span>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => fileInputRef.current?.click()}
                data-testid="aso-notify-add-attachment-btn"
              >
                <Paperclip className="w-3.5 h-3.5 mr-1" />
                Adicionar arquivo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="aso-notify-attachment-input"
              />
            </div>
            {attachments.length > 0 ? (
              <ul className="border rounded-md divide-y bg-white text-xs">
                {attachments.map((a, idx) => {
                  const isImg = /^image\//i.test(a.mime);
                  return (
                    <li key={idx} className="flex items-center justify-between px-2 py-1.5"
                        data-testid={`aso-notify-attachment-item-${idx}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        {isImg
                          ? <ImageIcon className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                          : <FileText className="w-3.5 h-3.5 text-red-600 shrink-0" />}
                        <span className="truncate text-slate-800">{a.name}</span>
                        <span className="text-slate-500 shrink-0">
                          · {humanSize(a.size)}
                        </span>
                      </span>
                      <button
                        onClick={() => removeAttachment(idx)}
                        className="text-slate-400 hover:text-red-600 p-1 rounded"
                        title="Remover anexo"
                        data-testid={`aso-notify-remove-attachment-${idx}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-[11px] text-slate-500 italic">
                Nenhum anexo adicional (o PDF gerado pela AVI é sempre enviado).
              </p>
            )}
          </div>

          {/* Selected preview */}
          <div className="border border-slate-200 rounded-md bg-slate-50 p-2 text-[11px] max-h-32 overflow-y-auto">
            <div className="font-semibold text-slate-700 mb-1">
              Funcionários no PDF ({selectedItems.length}):
            </div>
            <ul className="space-y-0.5 text-slate-600">
              {selectedItems.slice(0, 30).map((it) => (
                <li key={it.id} className="flex items-center justify-between">
                  <span>
                    <span className="font-mono">{it.matricula}</span>{" "}
                    · {it.nome}
                  </span>
                  <span className="text-slate-500">
                    {it.vencimento_aso ? isoToBr(it.vencimento_aso) : "—"}
                  </span>
                </li>
              ))}
              {selectedItems.length > 30 && (
                <li className="italic text-slate-500">
                  … e mais {selectedItems.length - 30} funcionário(s).
                </li>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}
            data-testid="aso-notify-cancel">
            Cancelar
          </Button>
          <Button
            onClick={send}
            disabled={sending || !emailTo.trim()}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="aso-notify-send"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar e-mail com PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
